<script setup>
import {computed, getCurrentInstance, watch, onMounted, ref} from 'vue'
import MessageTemplates from "../message/message-templates.vue";
import AppDistribute from "../system/app-distribute.vue";
import Notifications from "../notification/notifications.vue";
import ShipQuote from "../package/ship-quote.vue";
import User from "../user/user.vue";
import LookupWidget from "../lookup/lookup-widget.vue";
import HistoryModal from "../lookup/history-modal.vue";
import store from "../../store";
import helpers from "@/lib/helpers";
import {useRoute, useRouter} from "vue-router/composables";
import {breakpointsBootstrapV5, useBreakpoints} from "@vueuse/core";
import Warehouse from "../warehouse/warehouse.vue";
import DncModal from "../dnc/dnc-modal.vue";
import TagTool from "./tag-tool.vue";
import UserTimeclock from "@/components/timeclock/user-timeclock.vue";
import NotificationRules from "@/components/notification/notification-rules.vue";
import ChatModal from "@/components/chat/chat-modal.vue";
import GalleryModal from "@/components/gallery/gallery-modal.vue";
import FileStoreModal from "@/components/filestore/file-store-modal.vue";

const breakpoints = useBreakpoints(breakpointsBootstrapV5)
const large = breakpoints.greaterOrEqual('lg')
const small = breakpoints.smallerOrEqual('sm')

// What the person is looking at, so "this order" / "here" means something to the assistant.
const pageContext = () => {
  const path = route.fullPath
  if (path.startsWith('/chat')) return null

  const ctx = {path, title: route.meta?.title || document.title || ''}
  const rec = store.state.record
  const id = rec.active && rec.active.id
  // the record store can lag behind the route, so only trust it when the URL is that record's
  if (id && rec.type && route.path.startsWith(`/${rec.type}/${id}`)) {
    Object.assign(ctx, {type: rec.type, id, gli: rec.active.gli || null})
  }
  return ctx
}

const openAsk = () => {
  dialog.show(ChatModal, {context: pageContext()})
}

// --- the assistant icon: what it is doing in the background ---
const chatWorking = computed(() => store.getters.chat_working)
const chatReady = computed(() => !chatWorking.value && store.getters.chat_ready)
// finished answers not opened yet - the tiny count on the icon
const chatUnread = computed(() => store.getters.chat_active.filter(c => c.unread).length)

const samePlace = (ctx, here) => {
  if (!ctx || !here) return false
  if (ctx.gli && here.gli) return ctx.gli === here.gli
  return !!ctx.path && ctx.path === here.path
}

// chats still working or with an answer not yet opened - listed until each one is opened
const chatsPending = computed(() => {
  // re-evaluate when the page changes
  // eslint-disable-next-line no-unused-expressions
  route && route.fullPath
  const here = pageContext()
  return store.getters.chat_active.slice(0, 5).map(c => ({
    ...c,
    // a chat from the full assistant page carries no page context: it is "here" on its own page
    here: c.context ? samePlace(c.context, here) : !!route && route.path === `/chat/${c.id}`,
  }))
})

const chatPlace = (c) => {
  if (!c.context) return `the assistant${c.title ? ': ' + (c.title.length > 30 ? c.title.slice(0, 30) + '…' : c.title) : ''}`
  const ctx = c.context
  if (ctx.gli) return `${ctx.type ? ctx.type + ' ' : ''}${ctx.gli}`
  return ctx.title || c.title || 'your chat'
}

const askHereLabel = computed(() => {
  // eslint-disable-next-line no-unused-expressions
  route && route.fullPath
  const here = pageContext()
  if (here && here.gli) return `Ask about ${here.type ? here.type + ' ' : ''}${here.gli}`
  return 'Ask about this page'
})

// nothing working or waiting: the icon just opens the chat
const onAskShow = (e) => {
  if (!chatsPending.value.length) {
    e.preventDefault()
    openAsk()
  }
}

// back to the page the chat was on, then reopen that chat where it left off
const backToChat = async (c) => {
  // asked on the full assistant page: go back to that page, which picks the answer up itself
  if (!c.context && router) {
    await router.push(`/chat/${c.id}`).catch(() => {})
    return
  }
  const ctx = c.context || null
  if (router && ctx && ctx.path && ctx.path !== route.fullPath)
    await router.push(ctx.path).catch(() => {})
  dialog.show(ChatModal, {context: ctx, conversation: c.id})
}

const dialog = getCurrentInstance().proxy.$dialog

const links = [
  {
    label: 'Dashboard',
    match: '/dashboard',
    action: () => go('/dashboard'),
    perm: () => !large.value,
    root: true,
  },
  {
    label: 'Messages',
    match: '/message',
    action: () => go('/message'),
    perm: () => (helpers.can('messages.index') && helpers.can('messages.read')) || (helpers.can('messages.self')),
    badge: 'messages',
    root: true,
  },
  {
    label: 'Offers',
    action: () => go('/offer'),
    match: '/offer',
    perm: () => helpers.can('offers.index') && helpers.can('offers.read'),
    badge: 'offers',
    root: true,
  },
  {
    label: 'Orders',
    action: () => go('/order'),
    match: '/order',
    perm: () => helpers.can('orders.index') && helpers.can('orders.read'),
    root: true,
  },
  {
    label: 'Items',
    action: () => go('/item'),
    match: '/item',
    perm: () => helpers.can('items.index') && helpers.can('items.read'),
    root: true,
  },
  {
    label: 'Listings',
    match: '/auction',
    action: () => go('/auction'),
    perm: () => helpers.can('auctions.read') && helpers.can('auctions.index'),
    root: true,
  },
  {
    label: 'Reports',
    action: () => go('/report'),
    match: '/report',
    perm: () => helpers.can('report.index'),
    root: true,
  },
  {
    label: 'Time Cards',
    group: 'People',
    match: '/timeclock',
    action: () => go('/timeclock/' + (Array.isArray(store.state.settings.defaults.pay_schedules) ? store.state.settings.defaults.pay_schedules[0].name ?? null : null)),
    perm: () => helpers.can('users.timeclock.read') && store.state.settings.defaults.pay_schedules && store.state.settings.defaults.pay_schedules.length
  },
  {
    label: 'POs',
    group: 'Purchasing',
    action: () => go('/po'),
    match: '/po',
    perm: () => helpers.can('pos.index') && helpers.can('pos.read'),
  },
  {
    label: 'Bids',
    group: 'Purchasing',
    match: '/bid',
    action: () => go('/bid'),
    perm: () => helpers.can('bids.read') && helpers.can('bids.index')
  },
  {
    label: 'Events / Leads',
    group: 'Marketing',
    match: '/event',
    action: () => go('/event'),
    perm: () => helpers.can('events.read') && helpers.can('events.index')
  },
  {
    label: 'Campaigns',
    group: 'Marketing',
    match: '/mailbatch',
    action: () => go('/mailbatch'),
    perm: () => helpers.can('mail.batch.read') && helpers.can('mail.batch.index')
  },
  {
    label: 'Assistant',
    group: 'Tools',
    match: '/chat',
    action: () => go('/chat'),
    perm: () => helpers.can('chat.use')
  },
  {
    label: 'Customers',
    group: 'Sales',
    match: '/customer',
    action: () => go('/customer'),
    perm: () => helpers.can('customers.read') && helpers.can('customers.index')
  },
  {
    label: 'Quotes',
    group: 'Sales',
    match: '/quote',
    action: () => go('/quote'),
    perm: () => helpers.can('quotes.read') && helpers.can('quotes.index')
  },
  {
    label: 'Transactions',
    group: 'Sales',
    match: '/transaction',
    action: () => go('/transaction'),
    perm: () => helpers.can('transactions.read') && helpers.can('transactions.index')
  },
  {
    label: 'Vendors',
    group: 'Purchasing',
    match: '/vendor',
    action: () => go('/vendor'),
    perm: () => helpers.can('vendors.read') && helpers.can('vendors.index')
  },
  {
    label: 'Locations',
    group: 'Inventory',
    match: '/location',
    action: () => go('/location'),
    perm: () => helpers.can('locations.read') && helpers.can('locations.index')
  },
  /*
{
  label: 'Cycle Count Resolve',
  match: '/item/audit',
  action: () => go('/item/audit'),
  perm: () => helpers.can('items.read')
},
   */
  {
    label: 'Users',
    group: 'People',
    match: '/user',
    action: () => go('/user'),
    perm: () => helpers.can('users.index'),
  },
  {
    label: 'Do Not Contact',
    group: 'Marketing',
    action: () => dialog.show(DncModal),
    perm: () => helpers.can('dnc.read')
  },
  {
    label: 'Warehouses',
    group: 'Inventory',
    action: () => dialog.show(Warehouse),
    perm: () => helpers.can('warehouses.write')
  },
  {
    label: 'Flight Log',
    group: 'Tools',
    match: '/flights',
    action: () => go('/flights'),
    perm: () => helpers.can('flights.read')
  },
  {
    label: 'Ship Quote',
    group: 'Tools',
    action: () => dialog.show(ShipQuote),
    perm: () => true
  },
  {
    label: 'Listing Station',
    group: 'Tools',
    match: '/listing-station',
    action: () => go('/listing-station'),
    perm: () => helpers.can('report.index')
        && (helpers.can('auctions.create') || helpers.can('auctions.review'))
  },
  {
    label: 'Tag Tool',
    group: 'Tools',
    action: () => dialog.show(TagTool),
    perm: () => helpers.can('system.tagtool')
  },
  {
    label: 'RF App',
    group: 'Tools',
    action: () => helpers.rfApp(),
    perm: () => true
  },
]

/**
 * The gear menu: system setup, not day-to-day work. Everything a user actually
 * navigates to or runs stays in "More" - this is only for the handful of admin
 * screens that would otherwise dilute it.
 */
const tools = [
  {
    label: 'Message Templates', group: 'Setup',
    action: () => dialog.show(MessageTemplates),
    perm: () => helpers.can('messages.templates.write'),
  },
  {
    label: 'Receiving Scripts', group: 'Setup',
    action: () => router.push({name: 'receiving-scripts'}),
    perm: () => helpers.can('listing.scripts.read'),
  },
  {
    label: 'App Deploy', group: 'System',
    action: () => dialog.show(AppDistribute),
    perm: () => helpers.can('system.apps.read'),
  },
  {
    label: 'Configuration', group: 'System',
    action: () => router.push({name: 'system-config'}),
    // a permission per section (system.config.printers, ...): any one opens the page
    perm: () => (store.state.settings.permissions || []).some(p => p.startsWith('system.config.')),
  },
  {
    label: 'Devices and Printers', group: 'System',
    action: () => router.push({name: 'relay-service'}),
    perm: () => helpers.can('system.relay.read'),
  },
]

const lookup = ref(null)
const collapse = ref(false)
const isApp = getCurrentInstance().proxy.$router

const route = isApp ? useRoute() : null
const router = isApp ? useRouter() : null

const go = (link) => {
  router.push(link)
}
const singleLevelLinks = computed(() => {
  return !large.value || allowedLinks.value.filter(link => link.root).length <= 2
})

const allowedLinks = computed(() => {
  const pathname = route ? route.path : window.location.pathname

  return links.filter(link => link.perm()).map(link => {
    link.active = (pathname.indexOf(link.match) === 0)
    return link
  })
})

watch( computed(() => store.state.settings?.permissions), (v) => {
  if (Array.isArray(v) && v.length) {
    if (!navLinks.value.find(l => l.active))
      router.replace('/dashboard').catch(() => {})
  }
})

const navLinks = computed(() => {
  return singleLevelLinks.value ? allowedLinks.value : allowedLinks.value.filter(link => link.root)
})

const moreLinks = computed(() => {
  return singleLevelLinks.value ? [] : allowedLinks.value.filter(link => !link.root)
})

/**
 * Split an already permission-filtered list into labelled sections.
 *
 * Headers only earn their space once there is enough left to organise.
 * Permissions can cut either menu to two or three entries, and sections of one
 * under a header read as clutter, so below the threshold this returns a single
 * unlabelled section - which renders exactly as the old flat list did.
 *
 * Anything whose group is not in `order` falls into an unlabelled tail section,
 * so a new link without a group still appears rather than vanishing.
 */
const sectionize = (items, order, threshold) => {
  if (items.length <= threshold)
    return [{label: null, links: items}]

  const sections = order
      .map(label => ({label, links: items.filter(i => i.group === label)}))
      .filter(section => section.links.length)

  const tail = items.filter(i => !order.includes(i.group))
  if (tail.length)
    sections.push({label: null, links: tail})

  return sections
}

// Ordered by how the day runs: what you sell, how you market it, what you buy,
// where it sits, who does it, and the odds and ends.
const MORE_GROUPS = ['Sales', 'Marketing', 'Purchasing', 'Inventory', 'People', 'Tools']

// above the threshold the menu is too tall to scan, so categories become rows
// that open to the right; below it, one plain list is easier than either
const moreGrouped = computed(() => moreLinks.value.length > 7)

const moreSections = computed(() => sectionize(moreLinks.value, MORE_GROUPS, 7))

const toolLinks = computed(() => tools.filter(tool => tool.perm()))
const toolSections = computed(() => sectionize(toolLinks.value, ['Setup', 'System'], 3))

onMounted(async () => {
  refresh()
})

const refresh = () => {
  store.dispatch('badges_load')
}

const logout = () => {
  helpers.asyncAction('/api/login', 'DELETE').then(() => {
    store.commit('auth_logout')
    go('/login')
  })
}
</script>

<template>
  <b-navbar toggleable="lg" type="dark" class="navbar-color px-2 py-1 d-print-none">
    <b-navbar-brand>
      <img @click="go('/dashboard')" src='/images/logo-navbar.jpg' height="30"
           class="cursor-pointer d-none d-lg-inline">
    </b-navbar-brand>

    <div class="flex-fill" v-if="!large">
      <b-navbar-toggle class="my-1" target="nav-collapse"></b-navbar-toggle>
    </div>

    <b-collapse is-nav id="nav-collapse" @hidden="collapse=false" @show="collapse=true"
                :class="{'vh-100': collapse, 'overflow-auto': collapse}"
                :style="collapse ? 'padding-bottom: 80px' : ''">
      <b-navbar-nav>
        <b-nav-item v-for="nav in navLinks" :key="nav.label" href="#" @click="nav.action" :active="nav.active">
          {{ nav.label }}
          <b-badge variant="warning" v-if="nav.badge && $store.getters.get_badge(nav.badge)"
                   v-text="$store.getters.get_badge(nav.badge)"/>
        </b-nav-item>

        <!-- length, not !!moreLinks: an empty array is truthy, so a user with no
             permission for any of these used to get an empty "More" -->
        <b-nav-dropdown text="More" v-if="moreLinks.length">
          <!-- few enough to read at a glance: one plain list, no ceremony -->
          <template v-if="!moreGrouped">
            <b-dropdown-item v-for="nav in moreLinks" :key="nav.label" @click="() => nav.action()">
              {{ nav.label }}
              <b-badge variant="warning" v-if="nav.badge && $store.getters.get_badge(nav.badge)"
                       v-text="$store.getters.get_badge(nav.badge)"/>
            </b-dropdown-item>
          </template>

          <!-- long list: one row per category, opening to the right -->
          <template v-else>
            <template v-for="(section, i) in moreSections">
              <li v-if="section.label" :key="'sec-' + i" class="flyout">
                <!-- an anchor, not a span: focus-within is what opens the
                     submenu for keyboard users, and only a focusable element
                     can start that off -->
                <a class="dropdown-item flyout-label" href="#" @click.prevent>
                  {{ section.label }}
                  <font-awesome-icon :icon="['fas', 'chevron-right']" class="flyout-caret"/>
                </a>
                <ul class="dropdown-menu flyout-menu">
                  <li v-for="nav in section.links" :key="nav.label">
                    <a class="dropdown-item" href="#" @click.prevent="nav.action()">
                      {{ nav.label }}
                      <b-badge variant="warning" v-if="nav.badge && $store.getters.get_badge(nav.badge)"
                               v-text="$store.getters.get_badge(nav.badge)"/>
                    </a>
                  </li>
                </ul>
              </li>
              <!-- a link with no category still shows, at the foot of the menu -->
              <template v-else>
                <b-dropdown-item v-for="nav in section.links" :key="'tail-' + nav.label"
                                 @click="() => nav.action()">
                  {{ nav.label }}
                  <b-badge variant="warning" v-if="nav.badge && $store.getters.get_badge(nav.badge)"
                           v-text="$store.getters.get_badge(nav.badge)"/>
                </b-dropdown-item>
              </template>
            </template>
          </template>
        </b-nav-dropdown>
      </b-navbar-nav>
    </b-collapse>


    <template v-if="!collapse">
      <b-dropdown v-if="helpers.can('chat.use')" variant="link" toggle-class="text-light p-0 chat-toggle" no-caret right
                  @show="onAskShow">
        <template #button-content>
          <span class="chat-icon-wrap"
                :title="chatWorking ? 'The assistant is working on an answer' : (chatUnread ? chatUnread + ' answer' + (chatUnread > 1 ? 's' : '') + ' ready' : 'Ask the assistant')">
            <font-awesome-icon :icon="['fas', 'robot']" class="chat-icon"
                               :class="{'chat-icon-working': chatWorking, 'chat-icon-ready': chatReady}"/>
            <!-- answers not opened yet: the same badge shape as the bell, in green -->
            <b-badge v-if="chatUnread" variant="success" class="nav-icon-badge" v-text="chatUnread > 99 ? '99+' : chatUnread"/>
          </span>
        </template>
        <b-dropdown-item @click="openAsk">
          <font-awesome-icon :icon="['fas', 'comment']" class="mr-2 text-muted"/>{{ askHereLabel }}
        </b-dropdown-item>
        <b-dropdown-divider/>
        <b-dropdown-item v-for="c in chatsPending" :key="c.id" @click="backToChat(c)">
          <font-awesome-icon :icon="['fas', c.here ? 'comments' : 'arrow-left']" class="mr-2 text-muted"/>
          {{ c.here ? 'Open chat on' : 'Back to' }} {{ chatPlace(c) }}
          <span class="small ml-2" :class="c.running ? 'text-info' : 'text-success'">
            {{ c.running ? 'working…' : 'answer ready' }}
          </span>
        </b-dropdown-item>
      </b-dropdown>
      <b-link class="text-light ml-3" @click="$dialog.show(Notifications)">
        <font-awesome-icon :icon="['fas', 'bell']"/>
        <b-badge variant="warning" class="nav-icon-badge" v-if="!!$store.getters.get_badge('notifications')"
                 v-text="$store.getters.get_badge('notifications')"/>
      </b-link>

      <b-link class="text-light ml-3" @click="$dialog.show(HistoryModal)">
        <font-awesome-icon :icon="['fas', 'history']"/>
      </b-link>

      <b-dropdown toggle-class="text-light" variant="link" right no-caret v-if="toolLinks.length"
                  title="Tools and setup">
        <template #button-content>
          <font-awesome-icon :icon="['fas', 'gear']"/>
        </template>
        <template v-for="(section, i) in toolSections">
          <b-dropdown-divider v-if="i > 0" :key="'tool-div-' + i"/>
          <b-dropdown-header v-if="section.label" :key="'tool-hdr-' + i">{{ section.label }}</b-dropdown-header>
          <b-dropdown-item v-for="tool in section.links" :key="tool.label" @click="() => tool.action()">
            {{ tool.label }}
          </b-dropdown-item>
        </template>
      </b-dropdown>

      <b-dropdown toggle-class="text-light" variant="link" right no-caret v-if="small" @shown="$refs.lookup.focus()">
        <template #button-content>
          <font-awesome-icon :icon="['fas', 'magnifying-glass']"/>
        </template>
        <b-dropdown-form form-class="px-2 py-1">
          <lookup-widget ref="lookup"/>
        </b-dropdown-form>
      </b-dropdown>

      <b-dropdown toggle-class="text-light" variant="link" :text="$store.state.auth.user" right>
        <template v-if="can('users.timeclock.write') || can('users.timeclock.hourly')">
          <b-dropdown-item @click="$dialog.show(UserTimeclock)">Time Clock</b-dropdown-item>
          <b-dropdown-divider/>
        </template>
        <b-dropdown-item v-if="can('galleries.read')" @click="$dialog.show(GalleryModal)">Photo Galleries</b-dropdown-item>
        <b-dropdown-item v-if="can('filestore.read')" @click="$dialog.show(FileStoreModal)">File Store</b-dropdown-item>
        <b-dropdown-divider v-if="can('galleries.read') || can('filestore.read')"/>
        <b-dropdown-item @click="$dialog.show(User, {url: '/api/user/self'})">My Account</b-dropdown-item>
        <b-dropdown-item v-if="can('notifications.rules.read')" @click="$dialog.show(NotificationRules)">
          Notifications
        </b-dropdown-item>
        <b-dropdown-divider/>
        <b-dropdown-item @click="logout">Logout</b-dropdown-item>
      </b-dropdown>


      <lookup-widget v-if="!small"/>
    </template>
  </b-navbar>
</template>

<style scoped>
/* the count beside the assistant and bell icons: the same small size (the assistant sits in a
   dropdown button, whose larger font would otherwise make its badge bigger), a hair from the icon */
.nav-icon-badge {
  font-size: .65rem;
  margin-left: 3px;
  vertical-align: middle;
}

.chat-icon {
  transition: color .3s;
}

.chat-icon-wrap {
  position: relative;
  display: inline-block;
}

/* an answer is being written: a slow breathing pulse */
.chat-icon-working {
  animation: chat-think 1.6s ease-in-out infinite;
  color: #7cc4ff;
}

/* an answer is ready and unread */
.chat-icon-ready {
  color: #4ade80;
  filter: drop-shadow(0 0 4px rgba(74, 222, 128, .8));
}

@keyframes chat-think {
  0%, 100% { opacity: 1; transform: translateY(0) scale(1); }
  50% { opacity: .55; transform: translateY(-2px) scale(1.08); }
}

@media (prefers-reduced-motion: reduce) {
  .chat-icon-working {
    animation: none;
  }
}

.navbar-color {
  background-color: #2c3e50;
}

/* A category opens sideways instead of stacking, so "More" stays one short
   column however many permissions a user has. Only reachable on large screens:
   below lg the navbar collapses to a burger and singleLevelLinks puts every
   link in it, so no flyout is ever rendered at phone width. */
.flyout {
  position: relative;
}

.flyout-label {
  display: flex;
  align-items: center;
}

.flyout-label:hover,
.flyout-label:focus {
  background-color: #f8f9fa;
}

.flyout-caret {
  margin-left: auto;
  padding-left: 1.5rem;
  font-size: .7em;
  opacity: .45;
}

.flyout-menu {
  display: none;
  position: absolute;
  /* cancel the parent menu's top padding so the submenu's first row lines up
     with the category that opened it */
  top: -.5rem;
  left: 100%;
  right: auto;
  margin: 0;
  min-width: 12rem;
}

.flyout:hover > .flyout-menu,
.flyout:focus-within > .flyout-menu {
  display: block;
}
</style>